import { useState, useEffect } from "react";
import {
    AppBar,
    Toolbar,
    Typography,
    Button,
    Stack,
    Box,
    IconButton,
    Menu,
    MenuItem,
    Avatar,
    Container,
    Divider,
    ListItemIcon,
    useScrollTrigger,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import Logo from "./Logo";
import LoginDialog from "./LoginDialog";
import RegistrationDialog from "./RegistrationDialog";
import ResetPasswordDialog from "./ResetPasswordDialog";
import LanguageSwitcher from "./LanguageSwitcher";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useAuthContext } from "../auth/FirebaseContext";
import { resetBooking } from "../redux/slices/bookingSlice";
import { resetSearch } from "../redux/slices/searchSlice";

export default function Header() {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();

    const goHomeAndReset = () => {
        dispatch(resetSearch());
        dispatch(resetBooking());
        navigate("/");
    };
    const { isAuthenticated, user, logout } = useAuthContext();
    const [anchorEl, setAnchorEl] = useState(null);
    const [mobileAnchorEl, setMobileAnchorEl] = useState(null);

    const [loginOpen, setLoginOpen] = useState(false);
    const [registerOpen, setRegisterOpen] = useState(false);
    const [resetOpen, setResetOpen] = useState(false);

    const currentLang = (i18n.resolvedLanguage || i18n.language || "sr").substring(0, 2);
    const handleMobileOpen = (e) => setMobileAnchorEl(e.currentTarget);
    const handleMobileClose = () => setMobileAnchorEl(null);
    const handleMobileNav = (path) => { handleMobileClose(); navigate(path); };
    const handleSetLang = (code) => { i18n.changeLanguage(code); handleMobileClose(); };

    const isHome = location.pathname === "/";
    const trigger = useScrollTrigger({ disableHysteresis: true, threshold: 80 });
    const isTransparent = isHome && !trigger;

    const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);

    const handleLogout = async () => {
        handleMenuClose();
        await logout();
        navigate("/");
    };

    const openLogin = () => { setRegisterOpen(false); setResetOpen(false); setLoginOpen(true); };
    const openRegister = () => { setLoginOpen(false); setResetOpen(false); setRegisterOpen(true); };

    // Prompt za prijavu iz bilo kog dela app-a: ?login=1 otvara login dijalog
    // (koristi ga guard kupovine i „Kupi" na rezultatima), pa se parametar čisti.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get("login") === "1") {
            setRegisterOpen(false);
            setResetOpen(false);
            setLoginOpen(true);
            params.delete("login");
            navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search, location.pathname]);
    const openReset = () => { setLoginOpen(false); setRegisterOpen(false); setResetOpen(true); };

    return (
        <>
            <AppBar
                position="fixed"
                elevation={isTransparent ? 0 : 3}
                sx={{
                    bgcolor: isTransparent ? "transparent" : "rgba(255,255,255,0.97)",
                    color: isTransparent ? "white" : "text.primary",
                    backdropFilter: isTransparent ? "none" : "blur(8px)",
                    transition: "all 0.3s ease",
                    zIndex: 1100,
                }}
            >
                <Container maxWidth="lg">
                    <Toolbar sx={{ justifyContent: "space-between", px: { xs: 0 } }}>
                        <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                            sx={{ cursor: "pointer" }}
                            onClick={goHomeAndReset}
                        >
                            <Logo size={40} light={isTransparent} />
                            <Box>
                                {/* Brend u traci nije naslov sadržaja — kao <h6> je lomio
                                    redosled naslova strane (čitači ekrana ga čitaju kao nivo). */}
                                <Typography
                                    variant="h6"
                                    component="div"
                                    sx={{
                                        fontWeight: 800,
                                        lineHeight: 1.1,
                                        color: isTransparent ? "white" : "primary.main",
                                        letterSpacing: "-0.5px",
                                    }}
                                >
                                    BusGo
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        display: { xs: "none", md: "block" },
                                        color: isTransparent ? "rgba(255,255,255,0.8)" : "text.secondary",
                                        fontSize: "0.65rem",
                                        letterSpacing: "1px",
                                        textTransform: "uppercase",
                                    }}
                                >
                                    {t("header.tagline")}
                                </Typography>
                            </Box>
                        </Stack>

                        <Stack direction="row" spacing={1} alignItems="center" sx={{ display: { xs: "none", md: "flex" } }}>
                            <Button
                                onClick={() => navigate("/pomoc")}
                                startIcon={<Icon icon="mdi:help-circle-outline" />}
                                sx={{
                                    fontWeight: 600,
                                    color: isTransparent ? "white" : "text.primary",
                                }}
                            >
                                {t("header.help")}
                            </Button>
                            <LanguageSwitcher size="small" light={isTransparent} />
                            {isAuthenticated ? (
                                <>
                                    <Button
                                        onClick={() => navigate("/moje-karte")}
                                        startIcon={<Icon icon="mdi:ticket-account" />}
                                        sx={{
                                            fontWeight: 600,
                                            color: isTransparent ? "white" : "text.primary",
                                        }}
                                    >
                                        {t("header.myTickets")}
                                    </Button>
                                    <IconButton
                                        onClick={handleMenuOpen}
                                        aria-label={t("common.userMenu")}
                                        aria-haspopup="true"
                                        aria-expanded={Boolean(anchorEl)}
                                    >
                                        <Avatar
                                            src={user?.photoURL}
                                            alt={user?.displayName || user?.email || t("common.userAvatar")}
                                            sx={{
                                                width: 36,
                                                height: 36,
                                                bgcolor: isTransparent ? "rgba(255,255,255,0.25)" : "primary.main",
                                                fontSize: "0.9rem",
                                                fontWeight: 700,
                                            }}
                                        >
                                            {user?.displayName?.charAt(0) || user?.email?.charAt(0) || "K"}
                                        </Avatar>
                                    </IconButton>
                                    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                                        <MenuItem disabled>
                                            <Typography variant="body2" color="text.secondary">
                                                {user?.email}
                                            </Typography>
                                        </MenuItem>
                                        <MenuItem onClick={() => { handleMenuClose(); navigate("/moje-karte"); }}>
                                            <Icon icon="mdi:ticket-account" width={20} style={{ marginRight: 8 }} />
                                            {t("header.myTickets")}
                                        </MenuItem>
                                        <MenuItem onClick={() => { handleMenuClose(); navigate("/podesavanja"); }}>
                                            <Icon icon="mdi:cog-outline" width={20} style={{ marginRight: 8 }} />
                                            {t("header.settings")}
                                        </MenuItem>
                                        <MenuItem onClick={handleLogout}>
                                            <Icon icon="mdi:logout" width={20} style={{ marginRight: 8 }} />
                                            {t("header.logout")}
                                        </MenuItem>
                                    </Menu>
                                </>
                            ) : (
                                <>
                                    <Button
                                        variant={isTransparent ? "text" : "outlined"}
                                        onClick={openLogin}
                                        startIcon={<Icon icon="mdi:login" />}
                                        sx={{
                                            borderRadius: 2,
                                            color: isTransparent ? "white" : "primary.main",
                                            borderColor: isTransparent ? "rgba(255,255,255,0.5)" : undefined,
                                            "&:hover": {
                                                borderColor: isTransparent ? "white" : undefined,
                                                bgcolor: isTransparent ? "rgba(255,255,255,0.1)" : undefined,
                                            },
                                        }}
                                    >
                                        {t("header.login")}
                                    </Button>
                                    <Button
                                        variant="contained"
                                        onClick={openRegister}
                                        startIcon={<Icon icon="mdi:account-plus" />}
                                        sx={{
                                            borderRadius: 2,
                                            bgcolor: isTransparent ? "rgba(255,255,255,0.2)" : "primary.main",
                                            backdropFilter: "blur(4px)",
                                            "&:hover": {
                                                bgcolor: isTransparent ? "rgba(255,255,255,0.3)" : "primary.dark",
                                            },
                                        }}
                                    >
                                        {t("header.register")}
                                    </Button>
                                </>
                            )}
                        </Stack>

                        {/* Mobilni hamburger */}
                        <Box sx={{ display: { xs: "flex", md: "none" } }}>
                            <IconButton
                                onClick={handleMobileOpen}
                                aria-label="meni"
                                sx={{ color: isTransparent ? "white" : "text.primary" }}
                            >
                                <Icon icon="mdi:menu" width={28} />
                            </IconButton>
                            <Menu
                                anchorEl={mobileAnchorEl}
                                open={Boolean(mobileAnchorEl)}
                                onClose={handleMobileClose}
                                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                transformOrigin={{ vertical: "top", horizontal: "right" }}
                            >
                                <MenuItem onClick={() => handleMobileNav("/pomoc")}>
                                    <ListItemIcon><Icon icon="mdi:help-circle-outline" width={20} /></ListItemIcon>
                                    {t("header.help")}
                                </MenuItem>
                                <MenuItem onClick={() => handleMobileNav("/podesavanja")}>
                                    <ListItemIcon><Icon icon="mdi:cog-outline" width={20} /></ListItemIcon>
                                    {t("header.settings")}
                                </MenuItem>
                                {isAuthenticated && (
                                    <MenuItem onClick={() => handleMobileNav("/moje-karte")}>
                                        <ListItemIcon><Icon icon="mdi:ticket-account" width={20} /></ListItemIcon>
                                        {t("header.myTickets")}
                                    </MenuItem>
                                )}
                                <Divider />
                                <MenuItem onClick={() => handleSetLang("sr")} selected={currentLang === "sr"}>
                                    <ListItemIcon><Icon icon="twemoji:flag-serbia" width={20} /></ListItemIcon>
                                    Srpski
                                </MenuItem>
                                <MenuItem onClick={() => handleSetLang("en")} selected={currentLang === "en"}>
                                    <ListItemIcon><Icon icon="twemoji:flag-united-kingdom" width={20} /></ListItemIcon>
                                    English
                                </MenuItem>
                                <Divider />
                                {isAuthenticated ? (
                                    <MenuItem onClick={() => { handleMobileClose(); handleLogout(); }}>
                                        <ListItemIcon><Icon icon="mdi:logout" width={20} /></ListItemIcon>
                                        {t("header.logout")}
                                    </MenuItem>
                                ) : (
                                    [
                                        <MenuItem key="login" onClick={() => { handleMobileClose(); openLogin(); }}>
                                            <ListItemIcon><Icon icon="mdi:login" width={20} /></ListItemIcon>
                                            {t("header.login")}
                                        </MenuItem>,
                                        <MenuItem key="register" onClick={() => { handleMobileClose(); openRegister(); }}>
                                            <ListItemIcon><Icon icon="mdi:account-plus" width={20} /></ListItemIcon>
                                            {t("header.register")}
                                        </MenuItem>,
                                    ]
                                )}
                            </Menu>
                        </Box>
                    </Toolbar>
                </Container>
            </AppBar>

            <LoginDialog
                open={loginOpen}
                onClose={() => setLoginOpen(false)}
                onSwitchToRegister={openRegister}
                onSwitchToReset={openReset}
            />
            <RegistrationDialog
                open={registerOpen}
                onClose={() => setRegisterOpen(false)}
                onSwitchToLogin={openLogin}
            />
            <ResetPasswordDialog
                open={resetOpen}
                onClose={() => setResetOpen(false)}
                onSwitchToLogin={openLogin}
            />
        </>
    );
}
